import React from 'react'
import cx from 'classnames'
import { NavLink } from 'react-router-dom'
import { pure } from 'recompose'

import ChevronLeft from 'react-icons/lib/io/chevron-left'
import ChevronRight from 'react-icons/lib/io/chevron-right'
import PlusIcon from 'react-icons/lib/io/plus'
import CloseIcon from 'react-icons/lib/io/close'
import ImportIcon from 'react-icons/lib/io/android-download'
import ExternalIcon from 'react-icons/lib/io/android-open'
import DownloadIcon from 'react-icons/lib/md/file-download'
import SettingsIcon from 'react-icons/lib/io/android-options'
import FolderIcon from 'react-icons/lib/io/folder'
import PlayIcon from 'react-icons/lib/io/play'
import EditIcon from 'react-icons/lib/io/podium'
import RefreshIcon from 'react-icons/lib/io/refresh'

import { WaitIcon, getStatusMessage } from './StatusMessage'
import { getPlexusURL } from 'webmango-core/globals'

import './Controls.css'

export const ControlLink = function ControlLink ({ className, children, match, location, history, staticContext, ...props }) {
  const isOnlyIcon = checkIsOnlyIcon(children)
  children = wrapText(children)
  return (
    <NavLink className={cx('Control', className, { isOnlyIcon })} children={children} {...props} />
  )
}

export class ControlButton extends React.PureComponent {
  state = {
    error: undefined,
    isLoading: false
  }

  onClick = async (e) => {
    if (!this.props.onClick) return
    let isAsync = false
    let task = false
    try {
      this.setState({ error: undefined })
      task = this.props.onClick(e, this.state)
      if (task && task.then) {
        isAsync = true
      }
      if (isAsync && this._mounted) {
        this.setState({ isLoading: true, error: undefined })
        await task
      }
    } catch (error) {
      if (isAsync && this._mounted) this.setState({ error })
    } finally {
      if (isAsync && this._mounted) this.setState({ isLoading: false })
    }

    return task
  }

  componentWillMount () {
    this._mounted = true
  }

  componentWillUnmount () {
    this._mounted = false
  }

  render () {
    let { className, children, onClick, waitText, isLoading, errorText, title, ...props } = this.props
    const isOnlyIcon = checkIsOnlyIcon(children)
    let { error } = this.state
    isLoading = isLoading || this.state.isLoading

    let content = wrapText(children)
    if (!!isLoading && waitText) content = waitText
    if (!!error && errorText) content = errorText
    return (
      <button
        className={cx('Control', className, {
          isOnlyIcon,
          isLoading,
          disabled: !!(this.props.disabled || isLoading),
          error
        })}
        type='button'
        disabled={!!(this.props.disabled || isLoading)}
        children={content}
        onClick={this.onClick}
        title={error ? getStatusMessage(error) : title}
        {...props}
      />
    )
  }
}

function wrapText (children) {
  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      return <span className='content'>{child}</span>
    }
    return child
  })
}

function checkIsOnlyIcon (children) {
  return React.Children.toArray(children).reduce((v, child) => {
    if (!v) return v
    if (typeof child === 'string') {
      return child.trim() === ''
    } else {
      const { className = '' } = child.props
      return className.split(/\s+/).includes('Icon')
    }
  }, true)
}

export const PrevLink = pure(function PrevLink ({ className, children, ...props }) {
  return (
    <ControlLink className={cx('PrevLink', className)} {...props}>
      <ChevronLeft className={cx('Icon', { left: !!children })} />{ children || '' }
    </ControlLink>
  )
})

export const NextLink = pure(function NextLink ({ className, children, ...props }) {
  return (
    <ControlLink className={cx('NextLink', className)} {...props}>
      { children || '' }<ChevronRight className={cx('Icon', { right: !!children })} />
    </ControlLink>
  )
})

export const NewProcessLink = pure(function NewProcessLink ({ className, children, ...props }) {
  return (
    <ControlLink className={cx('NewProcessLink', className)} to='/create' {...props}>
      <PlusIcon className='Icon left' />{ children || 'New Process' }
    </ControlLink>
  )
})

export const NewProcessButton = pure(function NewProcessButton ({ className, children, ...props }) {
  return (
    <ControlButton title='New Process' className={cx('NewProcessButton', className)} {...props}>
      <PlusIcon className='Icon left' />{ children || 'New Process' }
    </ControlButton>
  )
})

export const NewSampleLink = pure(function NewSampleLink ({ className, children, ...props }) {
  return (
    <ControlLink className={cx('NewSampleLink', className)} to='/sample-import' {...props}>
      <ImportIcon className='Icon left' />{ children || 'Import New Sample' }
    </ControlLink>
  )
})

export const NewTabLink = pure(function NewTabLink ({ className, children, to, ...props }) {
  return (
    <a
      className={cx('Control NewTabLink', className)}
      href={to}
      target='_blank'
      rel='noopener noreferrer'
    >
      {children}
      <ExternalIcon className='Icon right' />
    </a>
  )
})

export const PlexusEntityLink = pure(function PlexusEntityLink ({ className, entityID, ...props }) {
  return (
    <PlexusLink className={cx('PlexusEntityLink', className)} href={`/entity/${entityID}`} {...props} />
  )
})

export const PlexusLink = pure(function PlexusLink ({ className, href, children, ...props }) {
  return (
    <a
      className={cx('Control PlexusLink', className)}
      href={getPlexusURL(`/${href}`)}
      target='_blank'
      rel='noopener noreferrer'
    >
      {children || 'View on Plexus'}
      <ExternalIcon className='Icon right' />
    </a>
  )
})

export const CloseLink = pure(function CloseLink ({ children, className, ...props }) {
  return (
    <ControlLink className={cx('CloseLink', className)} {...props}>
      <CloseIcon className={cx('Icon', { left: !!children })} />{ children || '' }
    </ControlLink>
  )
})

export const CloseButton = pure(function CloseButton ({ children, className, ...props }) {
  return (
    <ControlButton className={cx('CloseButton', className)} {...props}>
      <CloseIcon className={cx('Icon', { left: !!children })} />{ children || '' }
    </ControlButton>
  )
})

export const AddButton = pure(function AddButton ({ children, className, ...props }) {
  return (
    <ControlButton className={cx('AddButton', className)} {...props}>
      <PlusIcon className={cx('Icon', { left: !!children })} />{ children || '' }
    </ControlButton>
  )
})

export const DownloadLink = pure(function DownloadLink ({ className, to, children, ...props }) {
  return (
    <ControlLink className={cx('DownloadLink', className)} to={to} {...props}>
      <DownloadIcon className='Icon left' />{ children || 'Download' }
    </ControlLink>
  )
})

export const DownloadButton = pure(function DownloadButton ({ className, children, ...props }) {
  return (
    <ControlButton className={cx('DownloadButton', className)} {...props}>
      <DownloadIcon className='Icon left' />{ children || 'Download' }
    </ControlButton>
  )
})

export const DownloadFileLink = pure(function DownloadFileLink ({ className, to, children, ...props }) {
  return (
    <a className={cx('Control', 'DownloadFileLink', className)} target='_blank' rel='noopener noreferrer' href={to} {...props}>
      <DownloadIcon className='Icon left' />{ children || 'Download' }
    </a>
  )
})

export const RefreshButton = pure(function RefreshButton ({ className, children, ...props }) {
  return (
    <ControlButton className={cx('RefreshButton', className)} {...props}>
      <WaitIcon className='WaitIcon Icon left' />
      <RefreshIcon className='RefreshIcon Icon left' />{ children || '' }
    </ControlButton>
  )
})

export const SettingsLink = function SettingsLink ({ className, to, children, ...props }) {
  return (
    <ControlLink className={cx('SettingsLink', className)} to={to} {...props}>
      <SettingsIcon className={cx('Icon', { left: !!children })} />{ children || '' }
    </ControlLink>
  )
}

export const FilesLink = pure(function FilesLink ({ className, to, children, ...props }) {
  return (
    <ControlLink title='Browse' className={cx('FilesLink', className)} to={to} {...props}>
      <FolderIcon className={cx('Icon', { left: !!children })} />{ children || '' }
    </ControlLink>
  )
})

export const FilesButton = pure(function FilesButton ({ className, children, ...props }) {
  return (
    <ControlButton title='Browse' className={cx('FilesButton', className)} {...props}>
      <FolderIcon className={cx('Icon', { left: !!children })} />{ children || '' }
    </ControlButton>
  )
})

export const RunLink = function RunLink ({ className, to, children, ...props }) {
  return (
    <ControlLink className={cx('RunLink', className)} to={to} {...props}>
      <PlayIcon className={cx('Icon', { left: !!children })} />{ children || '' }
    </ControlLink>
  )
}

export const RunButton = function RunButton ({ className, children, ...props }) {
  return (
    <ControlButton className={cx('RunButton', className)} {...props}>
      <PlayIcon className={cx('Icon', { left: !!children })} />{ children || '' }
    </ControlButton>
  )
}

export const EditLink = function EditLink ({ className, to, children, ...props }) {
  return (
    <ControlLink className={cx('EditLink', className)} to={to} {...props}>
      <EditIcon className={cx('Icon', { left: !!children })} />{ children || '' }
    </ControlLink>
  )
}



// WEBPACK FOOTER //
// ./src/components/Controls.js